import { LICENSES, type Caps, type Openness } from './licenseInfo';

/**
 * The four choices the step actually asks: three plain-language levels, plus
 * the way out to the full CC list.
 *
 * A preset IS a license — picking one saves its `code`, so nothing downstream
 * knows the difference. The seven codes stay reachable under Custom, for the
 * user who came here knowing which one they want.
 */
export interface LicensePreset {
  id: string;
  /** The license this preset saves. Absent on Custom, which asks. */
  code?: string;
  title: string;
  blurb: string;
  /** The tag in the card's top corner. */
  tag: Openness | 'Custom';
  /** The line along the bottom — who this is for, not what it permits. */
  audience: string;
}

export const PRESETS: LicensePreset[] = [
  {
    id: 'open',
    code: 'CC BY-SA',
    title: 'Share & modify freely',
    blurb:
      'People can use, modify, and share your files, even commercially, as long as they follow the license terms.',
    tag: 'Most open',
    audience: 'Great for the community',
  },
  {
    id: 'sell',
    code: 'CC BY-ND',
    title: 'Sell finished products',
    blurb:
      'People can make and sell physical items, and pass on your files unchanged — but not share modified versions.',
    tag: 'Sort of open',
    audience: 'Good for makers & small business',
  },
  {
    id: 'personal',
    code: 'CC BY-NC-ND',
    title: 'Personal projects only',
    blurb:
      'People can make the project for themselves and pass your files on non-commercially. No modified versions, nothing sold.',
    tag: 'Most restrictive',
    audience: 'Best for personal use',
  },
  {
    id: 'custom',
    title: 'Choose my own permissions',
    blurb:
      'Set exactly what others can and cannot do with your files and finished products.',
    tag: 'Custom',
    audience: 'Fully customizable',
  },
];

/** The preset a saved code belongs to — one of the three, or Custom for any
 * other code, which is what reopens the full list on a resumed draft. */
export function presetFor(code: string): LicensePreset {
  return (
    PRESETS.find((preset) => preset.code === code) ??
    PRESETS[PRESETS.length - 1]
  );
}

/**
 * The four table columns. A preset's column is the caps of the LICENCE it
 * saves, not a hand-written list: "Share & modify freely" IS CC BY-SA, so the
 * two have to answer every row identically — stating them separately let the
 * same licence show two different tables.
 */
export const COMPARE_COLUMNS = PRESETS.map((preset) => ({
  preset,
  caps: LICENSES.find((l) => l.code === preset.code)?.caps as Caps | undefined,
}));
